/**
 * `<caos-test-result-table>` — what each test did, with the failure readable where it failed.
 * (Catalog: Developer surfaces › TestResultTable — per-method test results, with the message and stack trace opened in place.)
 *
 * FAILURES FIRST, ALWAYS, and it is not a sort preference. A run of four hundred tests with six
 * failures is read for the six; ordering by class name means scrolling for them, and ordering by
 * duration means a fast failure is buried under slow passes. Any column can still be sorted — the
 * duration column is how somebody finds what is making the suite slow — but failures lead until
 * somebody asks for something else.
 *
 * THE STACK TRACE OPENS IN PLACE. A failure whose message and trace live behind a modal is a failure
 * you read one at a time, and reading six failures usually means noticing what three of them have in
 * common — which you cannot do if you can only ever see one. Expanding a row keeps its neighbours on
 * screen.
 *
 * A TEST WITH NO OUTCOME IS NOT A PASS. A run that was aborted, or is still going, leaves rows whose
 * result is not yet known, and they read "not run" rather than joining the passes. A screen that
 * counts them as passing tells somebody their suite is green when a third of it never executed —
 * which is the exact failure mode the three rules of this project exist to prevent.
 *
 * Styling seams: `::part(results)`, `::part(summary)`, `::part(table)`, `::part(outcome)`,
 * `::part(detail)`, `::part(message)`, `::part(stack)`, `::part(expand)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import './data-table.js';
import './status-pill.js';
import type { CaosDataTable, CaosSortDirection, CaosTableRow } from './data-table.js';

/** How one test ended. `undefined` is a real state — see the header. */
export type CaosTestOutcome = 'pass' | 'fail' | 'skip';

/** One test's result. */
export interface CaosTestResult {
  readonly className: string;
  readonly method: string;
  /** ABSENT ⇒ not run: aborted, or still going. Never counted as a pass. */
  readonly outcome?: CaosTestOutcome;
  /** How long it took, in milliseconds. */
  readonly durationMs?: number;
  /** What went wrong. */
  readonly message?: string;
  /** Where it went wrong. */
  readonly stack?: string;
}

const sheet = makeSheet(`
  :host { display: block; }

  .results { display: flex; flex-direction: column; gap: var(--caos-space-3); }

  .summary { font-family: var(--caos-font); font-size: 12.5px; color: var(--caos-color-text-muted); }
  .summary strong { font-weight: 600; }
  .summary .fail { color: var(--caos-color-danger-text); }
  .summary .pass { color: var(--caos-color-success-text); }
  .summary .unrun { color: var(--caos-color-warning-text); }

  .empty { font-family: var(--caos-font); font-size: 13px; color: var(--caos-color-text-muted); }
`);

/** How many of each outcome, with "not run" counted separately from every other answer. */
export function tallyResults(results: readonly CaosTestResult[]): {
  pass: number;
  fail: number;
  skip: number;
  notRun: number;
} {
  const out = { pass: 0, fail: 0, skip: 0, notRun: 0 };
  for (const r of results) {
    if (r.outcome === 'pass') out.pass += 1;
    else if (r.outcome === 'fail') out.fail += 1;
    else if (r.outcome === 'skip') out.skip += 1;
    else out.notRun += 1;
  }
  return out;
}

/** Failures first, then not-run, then skipped, then passes — the order somebody reads a run in. */
const OUTCOME_RANK: Readonly<Record<string, number>> = { fail: 0, 'not-run': 1, skip: 2, pass: 3 };

export class CaosTestResultTable extends CaosElement {
  static get observedAttributes(): string[] {
    return ['sort-key', 'sort-direction', 'empty-text'];
  }

  private summaryEl: HTMLParagraphElement;
  private tableEl: CaosDataTable;
  private emptyEl: HTMLParagraphElement;

  private data: readonly CaosTestResult[] = [];
  private opened = new Set<string>();

  constructor() {
    super(sheet);

    const wrap = document.createElement('div');
    wrap.className = 'results';
    wrap.setAttribute('part', 'results');

    this.summaryEl = document.createElement('p');
    this.summaryEl.className = 'summary';
    this.summaryEl.setAttribute('part', 'summary');
    this.summaryEl.setAttribute('role', 'status');

    this.emptyEl = document.createElement('p');
    this.emptyEl.className = 'empty';
    this.emptyEl.hidden = true;

    this.tableEl = document.createElement('caos-data-table') as CaosDataTable;
    this.tableEl.setAttribute('part', 'table');
    this.tableEl.addEventListener('caos-sort', (e) => {
      const { column, direction } = (e as CustomEvent<{ column: string; direction: CaosSortDirection }>).detail;
      e.stopPropagation();
      this.setAttribute('sort-key', column);
      this.setAttribute('sort-direction', direction);
    });

    wrap.append(this.summaryEl, this.emptyEl, this.tableEl);
    this.root.append(wrap);
  }

  connectedCallback(): void {
    this.render();
  }

  attributeChangedCallback(): void {
    this.render();
  }

  get results(): readonly CaosTestResult[] {
    return this.data;
  }

  set results(next: readonly CaosTestResult[]) {
    this.data = Array.isArray(next) ? next : [];
    // A new run is a new run. Keeping the previous one's rows open would leave a stack trace on
    // screen belonging to a test that has since passed.
    this.opened.clear();
    this.render();
  }

  /** How many of each outcome. `notRun` is separate from every other answer, deliberately. */
  get tally(): { pass: number; fail: number; skip: number; notRun: number } {
    return tallyResults(this.data);
  }

  /** The failures, in the order they are shown — a re-run list, without re-deciding what failed. */
  get failures(): readonly CaosTestResult[] {
    return this.ordered.filter((r) => r.outcome === 'fail');
  }

  /** The results in the order they are drawn. */
  get ordered(): readonly CaosTestResult[] {
    const key = this.getAttribute('sort-key');
    const sign = this.getAttribute('sort-direction') === 'desc' ? -1 : 1;
    const name = (r: CaosTestResult): string => `${r.className}.${r.method}`;

    if (key === null || key === 'outcome') {
      // The default, and the case the header argues for: failures lead.
      return [...this.data].sort((a, b) => {
        const ra = OUTCOME_RANK[a.outcome ?? 'not-run'] ?? 9;
        const rb = OUTCOME_RANK[b.outcome ?? 'not-run'] ?? 9;
        if (ra !== rb) return (ra - rb) * (key === 'outcome' ? sign : 1);
        return name(a).localeCompare(name(b));
      });
    }
    if (key === 'durationMs') {
      return [...this.data].sort((a, b) => {
        // A test with no duration has not been timed. It sorts last in both directions rather than
        // reading as the fastest test in the run.
        const x = a.durationMs;
        const y = b.durationMs;
        if (x === undefined && y === undefined) return name(a).localeCompare(name(b));
        if (x === undefined) return 1;
        if (y === undefined) return -1;
        if (x !== y) return (x - y) * sign;
        return name(a).localeCompare(name(b));
      });
    }
    return [...this.data].sort((a, b) => {
      const read = (r: CaosTestResult): string => String((r as unknown as Record<string, unknown>)[key] ?? '');
      const cmp = read(a).localeCompare(read(b), undefined, { numeric: true, sensitivity: 'base' });
      return (cmp !== 0 ? cmp : name(a).localeCompare(name(b))) * sign;
    });
  }

  private toggle(key: string): void {
    if (this.opened.has(key)) this.opened.delete(key);
    else this.opened.add(key);
    this.render();
  }

  private render(): void {
    const empty = this.data.length === 0;
    this.emptyEl.hidden = !empty;
    this.tableEl.hidden = empty;
    this.emptyEl.textContent = this.getAttribute('empty-text') ?? 'No results yet.';

    const t = this.tally;
    const nodes: Node[] = [];
    const add = (cls: string, text: string): void => {
      const s = document.createElement('strong');
      s.className = cls;
      s.textContent = text;
      if (nodes.length > 0) nodes.push(document.createTextNode(' · '));
      nodes.push(s);
    };
    if (t.fail > 0) add('fail', `${t.fail} failed`);
    add('pass', `${t.pass} passed`);
    if (t.skip > 0) add('skip', `${t.skip} skipped`);
    // Stated every time there is one, and never folded into the passes. A green count over a run a
    // third of which never executed is the exact thing green is supposed to rule out.
    if (t.notRun > 0) add('unrun', `${t.notRun} not run`);
    this.summaryEl.replaceChildren(...nodes);

    if (empty) return;

    this.tableEl.columns = [
      { key: 'className', label: 'Class', sortable: true, width: '22%' },
      { key: 'method', label: 'Method', sortable: true, width: '26%' },
      { key: 'outcome', label: 'Result', sortable: true, width: '14%' },
      { key: 'durationMs', label: 'Duration', sortable: true, align: 'end', width: '12%' },
      { key: 'detail', label: 'Detail' },
    ];
    this.tableEl.sort = {
      key: this.getAttribute('sort-key') ?? 'outcome',
      direction: this.getAttribute('sort-direction') === 'desc' ? 'desc' : 'asc',
    };
    this.tableEl.rows = this.ordered.map((r) => this.rowFor(r));
  }

  private rowFor(r: CaosTestResult): CaosTableRow {
    const key = `${r.className}.${r.method}`;

    const pill = document.createElement('caos-status-pill');
    pill.setAttribute('part', 'outcome');
    pill.setAttribute(
      'intent',
      r.outcome === 'pass' ? 'success' : r.outcome === 'fail' ? 'danger' : r.outcome === 'skip' ? 'muted' : 'warning',
    );
    // The word carries it. The tone repeats it — "not run" is not a shade of grey somebody has to
    // interpret, it is two words.
    pill.textContent = r.outcome === undefined ? 'not run' : r.outcome === 'pass' ? 'passed' : r.outcome === 'fail' ? 'failed' : 'skipped';

    const duration = document.createElement('span');
    if (r.durationMs === undefined) {
      duration.className = 'caos-table__cell-note';
      duration.textContent = 'not timed';
    } else {
      duration.textContent = `${r.durationMs.toLocaleString()} ms`;
    }

    const detail = document.createElement('div');
    detail.setAttribute('part', 'detail');
    if (r.message === undefined && r.stack === undefined) {
      detail.className = 'caos-table__cell-note';
      detail.textContent = r.outcome === 'fail' ? 'no message was reported' : '';
    } else {
      const open = this.opened.has(key);
      const message = document.createElement('p');
      message.setAttribute('part', 'message');
      message.className = 'caos-table__detail-message';
      message.textContent = r.message ?? '';

      if (r.stack !== undefined) {
        const button = document.createElement('button');
        button.type = 'button';
        button.setAttribute('part', 'expand');
        button.className = 'caos-table__detail-toggle';
        button.textContent = open ? 'Hide stack trace' : 'Show stack trace';
        button.setAttribute('aria-expanded', open ? 'true' : 'false');
        button.setAttribute('aria-label', `${open ? 'Hide' : 'Show'} the stack trace for ${key}`);
        button.addEventListener('click', () => this.toggle(key));
        detail.append(message, button);
        if (open) {
          // In place, beneath its own row. Reading six failures usually means noticing what three of
          // them have in common, which a modal makes impossible.
          const stack = document.createElement('pre');
          stack.setAttribute('part', 'stack');
          stack.className = 'caos-table__detail-stack';
          stack.textContent = r.stack;
          detail.append(stack);
        }
      } else {
        detail.append(message);
      }
    }

    return {
      id: key,
      cells: {
        className: r.className,
        method: r.method,
        outcome: { node: pill },
        durationMs: { node: duration },
        detail: { node: detail },
      },
    } as CaosTableRow;
  }
}

define('caos-test-result-table', CaosTestResultTable);

declare global {
  interface HTMLElementTagNameMap {
    'caos-test-result-table': CaosTestResultTable;
  }
}
